import { type AuthUser } from "wasp/auth";
import DefaultLayout from "../layout/DefaultLayout";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Button } from "../../components/ui/button";
import { RefreshCw } from "lucide-react";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setReconnectDialogOpen } from "../../store/flowSlice";
import { shopifyBackendBaseUrl } from "./urls";
import { RootState } from "../../store/store";


const createWidgetDeepLink = (shopDomain: string) => {
  return `https://${shopDomain}/admin/themes/current/editor?context=apps&template=product&activateAppId=89d9407e-261a-4779-9877-244341882a1f/chat_bubble`;
}

const AdminSettingsPage = ({user}: {user: AuthUser}) => {
  const dispatch = useDispatch();
  const {currentCollection, currentUser} = useSelector((state: RootState) => state.user);
  const [isMiqLoading, setIsMiqLoading] = useState<boolean>(false);
  const [isGengageLoading, setIsGengageLoading] = useState<boolean>(false);
  const [isWidgetLoading, setIsWidgetLoading] = useState<boolean>(false);
  const [marketplaceAppInstalled, setMarketplaceAppInstalled] =
    useState<boolean>(false);
  const [gengageAppInstalled, setGengageAppInstalled] =
    useState<boolean>(false);
  const [widgetInstalled, setWidgetInstalled] = useState<boolean>(false);

  const checkAppInstallation = (setLoading: Dispatch<SetStateAction<boolean>>, app: string) => {
    const url = `${shopifyBackendBaseUrl}/checkAppInstallation`;
    setLoading(true);
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        shop: app === "miq" ? currentCollection?.collection?.miqShopifyDomain : currentUser?.gengageShopifyDomain,
        app: app,
      }),
    })
      .then((response) => {
        if (response.ok) {
          if (app == "miq") {
            setMarketplaceAppInstalled(true);
          } else if (app == "gengage") {
            setGengageAppInstalled(true);
          }
        }
      })
      .catch((error) => {
        console.error("Error checking app installation:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const checkMarketplaceAppInstalled = async () => {
    checkAppInstallation(setIsMiqLoading, "miq");
  };

  const checkGenGageAppInstalled = async () => {
    checkAppInstallation(setIsGengageLoading, "gengage");
  }

  const checkIfWidgetInstalled = async () => {
    const url = `${shopifyBackendBaseUrl}/checkWidgetInstallation`;
    setIsWidgetLoading(true);
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        shop: currentUser?.gengageShopifyDomain,
        app: "gengage",
        extensionId: "89d9407e-261a-4779-9877-244341882a1f",
      }),
    })
      .then(async (response) => {
        if (response.ok) {
          const data : {
            installed: boolean
          } = await response.json();
          console.log(data);
          if(data.installed){
            setWidgetInstalled(true);
          }else{
            setWidgetInstalled(false);
          }
        }else{
          setWidgetInstalled(false);
        }
      })
      .catch((error) => {
        console.error("Error checking widget installation:", error);
      })
      .finally(() => {
        setIsWidgetLoading(false);
      });
  }

  useEffect(() => {
    checkMarketplaceAppInstalled();
    checkGenGageAppInstalled();
    checkIfWidgetInstalled();
    
    const handleFocus = () => {
      checkMarketplaceAppInstalled();
      checkGenGageAppInstalled();
      checkIfWidgetInstalled();
    };

    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [currentCollection?.collection?.miqShopifyDomain, currentUser?.gengageShopifyDomain]);

  const handleAppInstall = (app: string) => {
    if (app == "miq") {
      dispatch(setReconnectDialogOpen(true));
    } else{
      const url = `${shopifyBackendBaseUrl}/authorize?shop=&redirectPath=${window.location.href}&app=${app}`;
      window.location.href = url;
    }
  };

  return (
    <DefaultLayout
      useWideLayout={true}
      user={user}
    >
      {" "}
      <div className="space-y-6 max-w-3xl mx-auto bg-white p-6 rounded-lg shadow">
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="flex items-center space-x-4">
            <Label htmlFor="app-installed">Marketplace App Installed</Label>
            {isMiqLoading ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Switch
                id="app-installed"
                checked={marketplaceAppInstalled}
                onCheckedChange={() => {}}
              />
            )}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => handleAppInstall("miq")}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Install App
          </Button>
        </div>
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="flex items-center space-x-4">
            <Label htmlFor="app-installed">Gengage App Installed</Label>
            {isGengageLoading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Switch
              id="app-installed"
              checked={gengageAppInstalled}
              onCheckedChange={() => {}}
            />}
         
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => handleAppInstall("gengage")}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Install App
          </Button>
        </div>

        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="flex items-center space-x-4">
            <Label htmlFor="app-installed">Gengage Widget Installed</Label>
            {isWidgetLoading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Switch
              id="app-installed"
              checked={widgetInstalled}
              onCheckedChange={() => {}}
            />}
         
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if(!user.gengageShopifyDomain) {
                alert("Please install Gengage App first");
                return;
              }
              window.open(createWidgetDeepLink(user.gengageShopifyDomain), '_blank');
            }}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Install Widget
          </Button>
        </div>
      </div>
    </DefaultLayout>
  );
};

export default AdminSettingsPage;
